import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../context/breadcrumb.context'
import { IconDocs } from '../../shared/icon/icon'
import './docs.scss'

// The public guides' building blocks. Every page is plain text for a
// customer's IT administrator: no sign-in, no API call, nothing stored.

export const DOCS_ROOT = '/public/docs'
export const SUPPORT_EMAIL = 'support@educationxr.com'

// Scrolls the guide's own box (never the page around it, whose header must
// stay put) so the element sits at its top.
const scrollBoxTo = (target, behavior) => {
  const box = target.closest('.edxr-docs')
  if (!box) return
  const top = target.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 16
  if (typeof box.scrollTo === 'function') box.scrollTo({ top, behavior })
  else box.scrollTop = top
}

// Moves to a heading and keeps its address (#id) in the bar for sharing,
// without a router navigation.
export const scrollToHeading = id => {
  const target = typeof document !== 'undefined' && document.getElementById(id)
  if (!target) return
  scrollBoxTo(target, 'smooth')
  if (window.history && window.history.replaceState) window.history.replaceState(window.history.state, '', `#${id}`)
}

const headingClick = id => event => {
  event.preventDefault()
  scrollToHeading(id)
}

// The "On this page" list: read from the article's h2s once it has rendered,
// so a section's title is written once.
const useHeadings = (articleRef, deps) => {
  const [headings, setHeadings] = useState([])

  useEffect(() => {
    const article = articleRef.current
    if (!article) return
    const found = Array.from(article.querySelectorAll('h2[id]')).map(h => ({
      id: h.id,
      title: (h.getAttribute('data-title') || h.textContent || '').trim()
    }))
    setHeadings(found)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return headings
}

// The heading whose section is being read, for the list's highlight.
const useActiveHeading = (scrollRef, headings) => {
  const [active, setActive] = useState()

  useEffect(() => {
    const box = scrollRef.current
    if (!box || !headings.length) return undefined
    const onScroll = () => {
      const top = box.getBoundingClientRect().top
      let current = headings[0].id
      for (const h of headings) {
        const el = document.getElementById(h.id)
        if (el && el.getBoundingClientRect().top - top <= 96) current = h.id
      }
      setActive(current)
    }
    onScroll()
    box.addEventListener('scroll', onScroll, { passive: true })
    return () => box.removeEventListener('scroll', onScroll)
  }, [scrollRef, headings])

  return active
}

const TocList = ({ headings, active }) => (
  <ol>
    {headings.map(h => (
      <li key={h.id}>
        <a href={`#${h.id}`} className={active === h.id ? 'active' : undefined} onClick={headingClick(h.id)}>
          {h.title}
        </a>
      </li>
    ))}
  </ol>
)

export const DocPage = ({ crumb, title, lead, updated, children, toc = true }) => {
  const scrollRef = useRef(null)
  const articleRef = useRef(null)
  const headings = useHeadings(articleRef, [title])
  const active = useActiveHeading(scrollRef, headings)
  const showToc = toc && headings.length > 2

  // Arriving with #section (a shared link): go there once the page is drawn.
  useEffect(() => {
    let id = ''
    try {
      id = decodeURIComponent((window.location.hash || '').slice(1))
    } catch (err) {
      id = ''
    }
    const target = id && document.getElementById(id)
    if (!target) return undefined
    // The browser's own jump to #id also scrolls the page around the box
    // (the header and footer slide away); put those back, then scroll the box.
    const settle = () => {
      for (let el = target.closest('.edxr-docs').parentElement; el; el = el.parentElement) {
        if (el.scrollTop) el.scrollTop = 0
      }
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0
      scrollBoxTo(target, 'auto')
    }
    settle()
    const timers = [60, 300, 900].map(ms => setTimeout(settle, ms))
    return () => timers.forEach(clearTimeout)
  }, [])

  return (
    <BreadcrumbView Icon={IconDocs} text={crumb || title}>
      <div className="edxr-docs" ref={scrollRef} data-testid="public-docs">
        <div className={`edxr-docs-inner${showToc ? ' has-toc' : ''}`}>
          <article className="doc-article" ref={articleRef}>
            <nav className="doc-crumbs" aria-label="Breadcrumb">
              <Link to={DOCS_ROOT}>EducationXR docs</Link>
              {crumb &&
                <React.Fragment>
                  <span aria-hidden="true">/</span>
                  <span aria-current="page">{crumb}</span>
                </React.Fragment>}
            </nav>
            <h1>{title}</h1>
            {lead && <p className="doc-lead">{lead}</p>}
            {updated && <p className="doc-meta">Last updated {updated}</p>}
            {showToc &&
              <details className="doc-toc doc-toc-inline">
                <summary>On this page</summary>
                <TocList headings={headings} active={active} />
              </details>}
            {children}
          </article>
          {showToc &&
            <nav className="doc-toc doc-toc-aside" aria-label="On this page">
              <div className="doc-toc-title">On this page</div>
              <TocList headings={headings} active={active} />
            </nav>}
        </div>
      </div>
    </BreadcrumbView>
  )
}

// A section: an h2 with its own address.
export const Section = ({ id, title, children }) => (
  <section aria-labelledby={id}>
    <h2 id={id} data-title={title}>
      {title}
      <a className="doc-anchor" href={`#${id}`} aria-label={`Link to ${title}`} onClick={headingClick(id)}>#</a>
    </h2>
    {children}
  </section>
)

export const Sub = ({ id, children }) => (
  <h3 id={id}>
    {children}
  </h3>
)

export const Callout = ({ tone = 'note', title, children, ...rest }) => (
  <div className={`doc-callout ${tone}`} role="note" {...rest}>
    {title && <strong className="doc-callout-title">{title}</strong>}
    {children}
  </div>
)

// A table that scrolls in its own box at phone width. `head` is the column
// titles; `rows` an array of cells (strings or elements).
export const Table = ({ head, rows, wide, caption }) => (
  <div className={`doc-table cols-${head.length}${wide ? ' wide' : ''}`} tabIndex={0} role="region" aria-label={caption || head.join(', ')}>
    <table>
      <thead>
        <tr>{head.map(h => <th key={h} scope="col">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => <td key={j} data-label={head[j]}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

export const C = ({ children }) => <code>{children}</code>

export const Mail = () => <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>

export const External = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
)
